import { useState } from 'react';
import { createBooking, createPaymentOrder, verifyPayment } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { X, Check, Loader2, Calendar, Clock, MapPin } from 'lucide-react';

export default function BookingModal({ event, onClose, onSuccess }) {
  const { user } = useAuth();
  const [step, setStep] = useState('form'); // form | paying | success
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      // Create booking
      const bookingRes = await createBooking({
        event_id: event.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes
      });
      const booking = bookingRes.data.booking || bookingRes.data;
      const bookingId = booking.id;

      // Create Razorpay order
      const orderRes = await createPaymentOrder({
        event_id: event.id,
        booking_id: bookingId
      });
      const orderData = orderRes.data;

      // Load Razorpay
      setStep('paying');
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Bunchh',
        description: orderData.event_title,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            setStep('success');
            toast.success('Payment successful! Your spot is confirmed.');
            if (onSuccess) onSuccess();
          } catch {
            toast.error('Payment verification failed. Contact support.');
            setStep('form');
          }
        },
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone
        },
        theme: { color: '#9d341d' },
        modal: {
          ondismiss: () => {
            setStep('form');
            toast.info('Payment cancelled');
          }
        }
      };

      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => new window.Razorpay(options).open();
        document.body.appendChild(script);
      } else {
        new window.Razorpay(options).open();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="booking-success-modal">
        <div className="modal-box" style={{ textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Check size={32} color="#166534" />
          </div>
          <h2 className="modal-title">You're in!</h2>
          <p className="modal-sub">Your spot for <strong>{event.title}</strong> is confirmed. We'll send you the details before the plan.</p>
          <button className="btn-coral" onClick={onClose} style={{ width: '100%' }} data-testid="booking-done-btn">Got it</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="booking-modal">
      <div className="modal-box">
        <button className="modal-close" onClick={onClose} data-testid="booking-modal-close"><X size={20} /></button>
        <div className="modal-eyebrow">Book your spot</div>
        <h2 className="modal-title">{event.title}</h2>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24, fontSize: 13, color: 'var(--text-secondary)' }}>
          {event.date && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={14} /> {event.date}</span>}
          {event.time && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={14} /> {event.time}</span>}
          {event.city && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={14} /> {event.city}</span>}
        </div>

        {event.price && (
          <div style={{ background: 'var(--cream-alt)', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Amount</span>
            <span style={{ fontFamily: 'Nunito', fontSize: 22, fontWeight: 800, color: 'var(--coral)' }}>{'\u20B9'}{event.price}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="booking-name" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required data-testid="booking-email" />
          </div>
          <div className="form-group">
            <label className="form-label">WhatsApp number</label>
            <input className="form-input" type="tel" placeholder="+91 00000 00000" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} required data-testid="booking-phone" />
          </div>
          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <textarea
              className="form-input"
              style={{ minHeight: 80, resize: 'vertical' }}
              placeholder="Anything we should know?"
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              data-testid="booking-notes"
            />
          </div>
          <button
            className="btn-coral"
            type="submit"
            disabled={loading || step === 'paying'}
            style={{ width: '100%' }}
            data-testid="booking-pay-btn"
          >
            {loading || step === 'paying' ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Loader2 size={18} className="animate-spin" /> Processing...
              </span>
            ) : (
              `Pay ${'\u20B9'}${event.price || 0} & Confirm`
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
